import React, {useEffect, useMemo} from 'react';
import {
	Checkbox,
	FormControl,
	FormErrorMessage,
	FormHelperText,
	FormLabel,
	Input,
	NumberDecrementStepper,
	NumberIncrementStepper,
	NumberInput,
	NumberInputField,
	NumberInputStepper,
	Select,
	PinInput,
	PinInputField,
	HStack,
	type InputProps,
} from '@tempo/core';
import type {FieldHookConfig} from 'formik';
import {ErrorMessage, useField, useFormikContext} from 'formik';
import {debounce} from '@app/modules/utils/debounce';
import {logClick} from '@app/modules/tracking/gtm';

type FormFieldProps<T> = FieldHookConfig<T> & {
	validateOnBlur?: boolean;
	validateOnChange?: boolean;
};
const useCustomField = <
	T extends string,
	H extends HTMLElement = HTMLInputElement,
>({
	validateOnBlur = false,
	validateOnChange = false,
	onChange: optionalOnChange,
	onBlur: optionalOnBlur,
	...props
}: FormFieldProps<T>) => {
	const {validateField} = useFormikContext();
	const [{onBlur, onChange, ...field}, meta, helpers] = useField(props);

	const isInvalid = Boolean(Boolean(meta.error) && meta.touched);
	// Debounce the whole validation as sometimes it makes http calls
	const debouncedValidate = useMemo(
		() => debounce(validateField),
		[validateField],
	);

	useEffect(() => () => debouncedValidate.cancel(), [debouncedValidate]);

	// We override the default event listeners to only validate our fields
	const handleBlur = async (e: React.FocusEvent<H>) => {
		if (validateOnBlur) debouncedValidate(props.name);
		optionalOnBlur?.(e as any);
		onBlur(e);
	};
	const handleChange = async (e: React.ChangeEvent<H>) => {
		if (validateOnChange) debouncedValidate(props.name);
		optionalOnChange?.(e as any);
		onChange(e);
	};
	return {handleBlur, handleChange, field, meta, helpers, isInvalid};
};

type ControlledFieldProps = {
	id?: string;
	name: string;
	label?: React.ReactNode;
	hint?: React.ReactNode;
	customErrorRenderer?: (msg: string) => React.ReactNode;
	color?: string;
};
export const ControlledField: React.FC<
	ControlledFieldProps & {
		isInvalid: boolean;
		children: React.ReactNode;
		fontSize?: string;
	}
> = ({
	isInvalid,
	children,
	id,
	name,
	label,
	hint,
	customErrorRenderer,
	color,
	fontSize,
}) => {
	const hintComponent: React.ReactNode = hint ? (
		React.isValidElement(hint) ? (
			hint
		) : (
			<FormHelperText data-testid={`${name}-hint`}>{hint}</FormHelperText>
		)
	) : null;

	return (
		<FormControl isInvalid={isInvalid}>
			{label && (
				<FormLabel
					data-testid={`${name}-label`}
					htmlFor={id ?? name}
					{...(color && {color})}
					{...(fontSize && {fontSize})}
				>
					{label}
				</FormLabel>
			)}
			{children}
			{hintComponent}
			<ErrorMessage
				name={name}
				data-testid={`${name}-error`}
				render={(msg) => {
					return (
						customErrorRenderer?.(msg) ?? (
							<FormErrorMessage data-testid={`${name}-error`} textAlign="start">
								{msg}
							</FormErrorMessage>
						)
					);
				}}
			/>
		</FormControl>
	);
};

export type FieldProps<T> = FormFieldProps<T> & {
	as?: React.ComponentType;
	type?: string;
	size?: 'sm' | 'md' | 'lg' | 'xs';
	'data-testid'?: string;
	placeholder?: string;
	clearApiError?: () => void;
} & ControlledFieldProps;
export const FormControlField = <T extends string>({
	as,
	label,
	id,
	size,
	children,
	type,
	clearApiError,
	...props
}: FieldProps<T>) => {
	const {handleChange, handleBlur, field, isInvalid} = useCustomField(props);
	const AsComponent = as ?? Input;
	id ??= props.name;

	const onClick = () => {
		logClick({
			eventaction: `click-${props.name}-field`,
		});
	};

	const inputProps: InputProps = {
		...field,
	};

	if (type) {
		inputProps.type = type;
	}

	return (
		<ControlledField label={label} id={id} isInvalid={isInvalid} {...props}>
			<AsComponent
				autoComplete={props.autoComplete ?? props.name}
				data-cy={`input-field-${props.name}`}
				data-testid={props['data-testid']}
				id={id}
				onBlur={handleBlur}
				onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
					handleChange(e);
					clearApiError?.();
				}}
				onClick={onClick}
				placeholder={props.placeholder}
				size={size}
				{...inputProps}
			/>
			{children}
		</ControlledField>
	);
};
export type NumberFormControlProps = FieldProps<string> &
	Partial<{constraint?: {min?: number; max?: number}}>;
export const NumberFormControlField: React.FC<NumberFormControlProps> = ({
	label,
	id,
	constraint,
	...props
}) => {
	const {
		handleBlur,
		field,
		isInvalid,
		helpers: {setValue},
	} = useCustomField(props);
	id ??= props.name;

	const onClick = () => {
		logClick({
			eventaction: `click-${props.name}-field`,
		});
	};

	return (
		<ControlledField id={id} isInvalid={isInvalid} label={label} {...props}>
			<NumberInput
				id={id}
				data-cy={`number-field-${props.name}`}
				max={constraint?.max}
				min={constraint?.min}
				onChange={(val) => {
					setValue(val, props.validateOnChange);
				}}
				onClick={onClick}
				onBlur={handleBlur}
				{...field}
			>
				<NumberInputField
					data-testid={props['data-testid']}
					placeholder={props.placeholder ?? ''}
				/>
				<NumberInputStepper
					sx={{
						svg: {
							height: '16px',
							width: '16px',
						},
					}}
				>
					<NumberIncrementStepper />
					<NumberDecrementStepper />
				</NumberInputStepper>
			</NumberInput>
		</ControlledField>
	);
};

export const BooleanFormControlField = <T extends string>({
	label,
	id,
	type = 'checkbox',
	...props
}: FieldProps<T>) => {
	const {handleBlur, handleChange, field, isInvalid} = useCustomField({
		type,
		...props,
	});
	id ??= props.name;

	const onClick = () => {
		logClick({
			eventaction: `click-${props.name}-field`,
		});
	};

	return (
		<ControlledField
			id={id}
			data-cy={`boolean-field-${props.name}`}
			isInvalid={isInvalid}
			{...props}
		>
			<Checkbox
				id={id}
				data-testid={props['data-testid']}
				onChange={handleChange}
				onClick={onClick}
				onBlur={handleBlur}
				{...field}
			>
				{label}
			</Checkbox>
		</ControlledField>
	);
};

export const SelectFormControlField = <T extends string>({
	label,
	type = 'checkbox',
	children,
	id,
	...props
}: React.PropsWithChildren<FieldProps<T>>) => {
	id ??= props.name;
	const {handleBlur, handleChange, field, isInvalid} = useCustomField<
		T,
		HTMLSelectElement
	>({
		type,
		...props,
	});

	const onClick = () => {
		logClick({
			eventaction: `click-${props.name}-field`,
		});
	};

	return (
		<ControlledField
			data-cy={`selct-field-${props.name}`}
			isInvalid={isInvalid}
			label={label}
			id={id}
			{...props}
		>
			<Select
				id={id}
				onBlur={handleBlur}
				onChange={handleChange}
				onClick={onClick}
				data-testid={props['data-testid']}
				{...field}
			>
				{children}
			</Select>
		</ControlledField>
	);
};

export const PinInputControlField = <T extends string>({
	label,
	children,
	id,
	pinNumberLength,
	...props
}: React.PropsWithChildren<FieldProps<T>> & {pinNumberLength: number}) => {
	id ??= props.name;
	const {setFieldValue} = useFormikContext();
	const {isInvalid} = useCustomField<T, HTMLSelectElement>({
		...props,
	});
	return (
		<ControlledField
			data-cy={`select-field-${props.name}`}
			isInvalid={isInvalid}
			label={label}
			id={id}
			{...props}
		>
			<HStack>
				<PinInput
					type="alphanumeric"
					onChange={(value) => setFieldValue(props.name, value)}
				>
					{Array.from({length: pinNumberLength}, (_, index) => (
						<PinInputField
							key={index}
							data-testid={`field-${props.name}-${index}`}
						/>
					))}
				</PinInput>
			</HStack>
		</ControlledField>
	);
};
